/**
 * 往上翻更早的消息（I 2026-09-27 定的翻页规格，后端 06:13 落）：
 * - 不带 cursor 的最新一页照旧归 thread 查询（15 秒轮询，后端据此记已读）；这里只管更早的几页，一律带 cursor 取，后端不记已读。
 * - 游标不透明：原样放回 ?cursor=，不解析。next_cursor 为 null 就是没有更早的了（消息区最上面写明）。
 * - 坏游标（400，details.reason = "cursor_invalid"；游标短命，过期也这样）：从最新一页重来——重取最新一页，顺着 next_cursor 往回走到
 *   已经显示的最早那条（最多比原来多走两页），已经显示的消息不会消失。重来之后下一页又坏，就停下：不再自动往上加载，
 *   写明“暂时取不到”，只有点“再试一次”才重来，不会死循环。别的错误（断网等）同样停在“没取到 · 再试一次”，不自动重试。
 * - 更早的几页存在 React Query 缓存里（键 = 这只宠物的消息缓存键 + "older"）：换宠物、换账号各是各的；退出、换家清缓存时一起清。
 *   它没有取数函数（enabled: false），发消息后的失效不会把它重取。
 * - 第一次往上翻时把当时的最新一页一起存进去：之后新消息把最新一页往后推，掉出去的几条仍接在更早的几页后面，中间不断档。
 *   两次刷新之间来了一整页以上的新消息（新的最新一页和已有的一条都接不上）：中间那段没取过，宁可把更早的几页丢掉、从新的最新一页重新往上翻，也不留一截空档。
 */
import { useCallback, useLayoutEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MessageSummary, MessageThread } from "@/shared/contracts";
import { isApiError } from "@/shared/api/errors";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";

export type OlderStatus = "idle" | "loading" | "failed" | "stopped";
export interface OlderPages {
  /** 更早的几页 + 第一次往上翻时的最新一页，按时间先后 */
  items: MessageSummary[];
  /** 再往前一页的游标；null = 没有更早的了 */
  cursor: string | null;
  /** 已经往上取了几页（重来时最多多走两页） */
  pages: number;
  status: OlderStatus;
  /** 刚从最新一页重来过：再遇到坏游标就停 */
  restarted: boolean;
}
const EMPTY: OlderPages = { items: [], cursor: null, pages: 0, status: "idle", restarted: false };

export function isCursorInvalid(error: unknown): boolean {
  return isApiError(error) && error.status === 400 && error.details?.reason === "cursor_invalid";
}

const isReadDenied = (error: unknown): boolean => isApiError(error) && (error.status === 401 || error.status === 403);

const uniq = (list: MessageSummary[]) => {
  const seen = new Set<string>();
  return list.filter((m) => !seen.has(m.message_id) && Boolean(seen.add(m.message_id)));
};
/** 出现在对话里的时刻（契约：排序、分组都按 available_at；旧数据没有时退回 created_at） */
const shownAt = (m: MessageSummary) => Date.parse(m.available_at ?? m.created_at) || 0;
const byTime = (list: MessageSummary[]) => list.map((m, i) => ({ m, i })).sort((a, b) => shownAt(a.m) - shownAt(b.m) || a.i - b.i).map(({ m }) => m);

/** 显示用：更早的几页在前、最新一页在后；同一条只留一份，最新一页里有的用最新一页的（送达状态最新） */
export function mergeThread(older: MessageSummary[], latest: MessageSummary[]): MessageSummary[] {
  if (!older.length) return latest;
  const inLatest = new Set(latest.map((m) => m.message_id));
  return [...uniq(older.filter((m) => !inLatest.has(m.message_id))), ...latest];
}

export interface OlderMessages {
  items: MessageSummary[];
  hasMore: boolean;
  status: OlderStatus;
  /** 滚到最上面时自动调：只有 idle 且还有更早的才取 */
  loadMore: () => void;
  /** 点“查看更早的消息 / 再试一次”：失败、停下之后也能再来一次 */
  retry: () => void;
}

/** Later observed payloads replace earlier ones; this is local read ordering, not a server revision. */
const updateRows = (...groups: MessageSummary[][]): MessageSummary[] => {
  const rows = new Map<string, MessageSummary>();
  for (const group of groups) for (const row of group) rows.set(row.message_id, row);
  return byTime([...rows.values()]);
};

export function useOlderMessages({ userId, petId, latest, refetchLatest, scope, enabled = true }: { userId: string; petId: string | undefined; latest: MessageThread | undefined; refetchLatest: () => Promise<MessageThread | undefined>; scope?: string; enabled?: boolean }): OlderMessages {
  const { communicator } = useServices();
  const client = useQueryClient();
  const key = useMemo(() => [...queryKeys.messagesFor(userId, petId ?? "-"), "older"] as const, [userId, petId]);
  const observed = useQuery({ queryKey: key, queryFn: () => EMPTY, initialData: EMPTY, enabled: false, staleTime: Infinity }).data;
  // Subscribe through the observer, but project the current cache: its batched
  // notification can still hold old data when the latest query already recovered.
  const older = observed && (client.getQueryData<OlderPages>(key) ?? EMPTY);
  // A separate lifetime per scope also rejects A→B→A callbacks. No API AbortSignal is invented.
  const life = useMemo(() => ({ alive: true, busy: false, generation: 0, latest: undefined as MessageThread | undefined, laterLatest: new Map<string, MessageSummary>() }), [userId, petId, scope, enabled, client, communicator]);
  useLayoutEffect(() => {
    life.alive = true;
    return () => {
      life.alive = false; life.generation++; life.busy = false; life.laterLatest.clear();
      // Do not recreate a cache removed by logout. Allow a later valid visit to resume a cancelled read.
      const saved = client.getQueryData<OlderPages>(key);
      if (saved?.status === "loading") client.setQueryData<OlderPages>(key, { ...saved, status: "idle" });
    };
  }, [life, client, key]);

  // A confirmed denial invalidates the earlier history read, not just its current
  // projection. Observe the exact latest query synchronously: denial and success
  // can occur in one React batch, or the route can unmount before an effect sees it.
  const invalidateHistory = useCallback(() => {
    life.generation++; life.busy = false; life.laterLatest.clear();
    const saved = client.getQueryData<OlderPages>(key);
    // Never recreate a cache removed by logout. Do not write identical state in
    // response to cache notifications, which would produce a notification loop.
    if (saved && (saved.items.length || saved.cursor || saved.pages || saved.restarted || saved.status !== "failed")) {
      client.setQueryData<OlderPages>(key, { ...EMPTY, status: "failed" });
    }
  }, [life, client, key]);
  useLayoutEffect(() => {
    const cache = client.getQueryCache();
    const latestKey = queryKeys.messagesFor(userId, petId ?? "-");
    const latestHash = JSON.stringify(latestKey), olderHash = JSON.stringify(key);
    let rejectedQuery: object | undefined, rejectedCount = -1;
    const observe = () => {
      const query = cache.find({ queryKey: latestKey, exact: true });
      if (!query || query.state.status !== "error" || !isReadDenied(query.state.error)) return;
      if (query === rejectedQuery && query.state.errorUpdateCount === rejectedCount) return;
      rejectedQuery = query; rejectedCount = query.state.errorUpdateCount;
      invalidateHistory();
    };
    const unsubscribe = cache.subscribe(event => {
      const hash = JSON.stringify(event.query.queryKey);
      if (event.type === "removed" && hash === olderHash) {
        life.generation++; life.busy = false; life.laterLatest.clear();
      } else if (hash === latestHash) observe();
    });
    observe();
    return unsubscribe;
  }, [client, key, userId, petId, life, invalidateHistory]);

  useLayoutEffect(() => {
    const before = life.latest;
    life.latest = enabled && latest?.pet_id === petId ? latest : undefined;
    const current = life.latest;
    if (!current || before === current) return;
    if (life.busy) for (const row of current.items) life.laterLatest.set(row.message_id, row);
    const now = client.getQueryData<OlderPages>(key) ?? EMPTY;
    if (!now.items.length) return;
    const known = new Set([...now.items, ...(before?.items ?? [])].map(m => m.message_id));
    if (before && current.next_cursor && current.items.length && !current.items.some(m => known.has(m.message_id))) {
      life.generation++; life.busy = false; life.laterLatest.clear();
      client.setQueryData<OlderPages>(key, EMPTY);
      return;
    }
    const cached = new Set(now.items.map(m => m.message_id));
    const kept = new Set([...now.items, ...current.items].map(m => m.message_id));
    const dropped = before?.items.filter(m => !kept.has(m.message_id)) ?? [];
    const updates = current.items.filter(m => cached.has(m.message_id));
    if (updates.length || dropped.length) client.setQueryData<OlderPages>(key, { ...now, items: updateRows(now.items, dropped, updates) });
  }, [latest, enabled, petId, life, client, key]);

  const run = useCallback(async () => {
    if (!enabled || !life.alive || life.busy || !petId || !life.latest) return;
    const saved = client.getQueryData<OlderPages>(key) ?? EMPTY;
    const base: OlderPages = saved.items.length ? saved : { ...EMPTY, items: life.latest.items, cursor: life.latest.next_cursor };
    if (!base.cursor) return;
    life.busy = true; life.laterLatest.clear();
    const token = ++life.generation;
    const active = () => life.alive && life.generation === token;
    const present = () => client.getQueryData<OlderPages>(key) ?? base;
    const put = (next: OlderPages) => { if (active()) client.setQueryData<OlderPages>(key, next); };
    const checked = (page: MessageThread | undefined): MessageThread => {
      if (!page || page.pet_id !== petId) throw new Error("这只宠物的消息没取到");
      return page;
    };
    put({ ...base, status: "loading" });
    try {
      const page = checked(await communicator.thread(petId, base.cursor));
      if (!active()) return;
      put({ items: updateRows(present().items, page.items, [...life.laterLatest.values()]), cursor: page.next_cursor, pages: base.pages + 1, status: "idle", restarted: false });
    } catch (error) {
      if (!active()) return;
      if (isReadDenied(error)) invalidateHistory();
      else if (!isCursorInvalid(error)) put({ ...present(), status: "failed" });
      else if (base.restarted) put({ ...present(), status: "stopped" });
      else {
        try {
          const fresh = checked(await refetchLatest());
          if (!active()) return;
          const target = base.items[0]?.message_id;
          let items = [...fresh.items];
          let cursor = fresh.next_cursor;
          let pages = 0;
          while (cursor && pages < base.pages + 2 && !(target && items.some(m => m.message_id === target))) {
            const page = checked(await communicator.thread(petId, cursor));
            if (!active()) return;
            items = updateRows(items, page.items);
            cursor = page.next_cursor; pages++;
          }
          put({ items: updateRows(present().items, items, [...life.laterLatest.values()]), cursor, pages, status: "idle", restarted: true });
        } catch (error) {
          if (active()) {
            if (isReadDenied(error)) invalidateHistory();
            else put({ ...present(), status: "stopped" });
          }
        }
      }
    } finally {
      // An old operation must not unlock a newer scope or post-gap read.
      if (active()) { life.busy = false; life.laterLatest.clear(); }
    }
  }, [enabled, life, client, communicator, key, petId, refetchLatest, invalidateHistory]);

  const hasMore = enabled && (older.items.length ? older.cursor !== null : Boolean(latest && latest.pet_id === petId && latest.next_cursor));
  const loadMore = useCallback(() => { if (older.status === "idle" && hasMore) void run(); }, [older.status, hasMore, run]);
  const retry = useCallback(() => {
    if (!enabled || !life.alive || life.busy) return;
    const now = client.getQueryData<OlderPages>(key);
    if (now && (now.status === "failed" || now.status === "stopped")) client.setQueryData<OlderPages>(key, { ...now, status: "idle", restarted: false });
    void run();
  }, [enabled, life, client, key, run]);
  return { items: older.items, hasMore, status: older.status, loadMore, retry };
}
